/**
 * SectionGridItem Component
 *
 * Displays a single section box in the session view with:
 * - Status-based color coding (RED/ORANGE/GREEN)
 * - Thumbnail preview of uploaded image(s)
 * - Section name and status label
 * - Click handlers for empty vs. populated sections
 *
 * Status Colors:
 * - RED border/bg: No images uploaded (empty)
 * - ORANGE border/bg: Images uploaded but validation has discrepancies
 * - GREEN border/bg: Images uploaded and validation passes
 */

import React from 'react';
import { RoomSection, HistoryItem } from '@/types';
import { Package, CheckCircle, AlertCircle, Upload, Info } from 'lucide-react';
import {
  SectionStatus,
  getSectionStatusColors,
  getSectionStatusLabel,
} from '@/utils/sectionStatus';

export interface ValidationCounts {
  match: number;
  mismatch: number;
  missing: number;
  unexpected: number;
}

// Validation count colors matching the legend
const VALIDATION_COLORS = {
  match: 'text-emerald-600',
  mismatch: 'text-amber-500',
  missing: 'text-red-400',
  unexpected: 'text-blue-400',
};

interface SectionGridItemProps {
  section: RoomSection;
  images: HistoryItem[];
  status: SectionStatus;
  isSelected: boolean;
  onClick: () => void;
  onEmptyClick: () => void;
  loadImage: (id: number) => Promise<string | null>;
  onValidationClick?: () => void; // Optional callback to show validation results
  onInfoClick?: () => void; // Optional callback to show section details
  validationCounts?: ValidationCounts | null; // Validation counts for display
}

export const SectionGridItem: React.FC<SectionGridItemProps> = ({
  section,
  images,
  status,
  isSelected,
  onClick,
  onEmptyClick,
  loadImage,
  onValidationClick,
  onInfoClick,
  validationCounts,
}) => {
  const [imageSrc, setImageSrc] = React.useState<string | null>(null);

  // Load the first image when component mounts or images change
  React.useEffect(() => {
    if (images.length > 0 && images[0].id) {
      loadImage(images[0].id).then(setImageSrc);
    }
  }, [images, loadImage]);
  const colors = getSectionStatusColors(status);
  const statusLabel = getSectionStatusLabel(status);

  // Get status icon
  const StatusIcon = status === 'empty'
    ? Upload
    : status === 'mismatch'
    ? AlertCircle
    : CheckCircle;

  // Handle click based on whether section has images
  const handleClick = () => {
    if (images.length === 0) {
      onEmptyClick(); // Auto-select and open file picker
    } else {
      onClick(); // Show image in viewer
    }
  };

  // Show re-upload button if there are validation issues
  const showReuploadButton = (status === 'mismatch' || status === 'empty') && images.length > 0;

  return (
    <div
      className={`
        w-full p-3 rounded-lg border-2 transition-all cursor-pointer relative group
        ${colors.border} ${colors.background}
        ${isSelected ? 'ring-2 ring-primary-500 ring-offset-2' : 'hover:shadow-md'}
      `}
    >
      {/* Section Header */}
      <div className="flex items-start justify-between mb-2">
        <div
          onClick={handleClick}
          className="flex-1 min-w-0 cursor-pointer"
        >
          <div className="flex items-center gap-1.5">
            <div className="font-medium text-gray-900 truncate">{section.name}</div>
            {/* Info icon to show section details */}
            {onInfoClick && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onInfoClick();
                }}
                className="text-gray-400 hover:text-primary-600 transition-colors flex-shrink-0"
                title="View section details"
              >
                <Info className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Status Badge - Clickable when validation is available */}
          <div
            className={`text-xs flex items-center gap-1 mt-0.5 ${colors.text} ${
              images.length > 0 && onValidationClick ? 'cursor-pointer hover:underline' : ''
            }`}
            onClick={(e) => {
              if (images.length > 0 && onValidationClick) {
                e.stopPropagation();
                onValidationClick();
              }
            }}
            title={images.length > 0 && onValidationClick ? 'Click to view validation details' : ''}
          >
            <StatusIcon className="h-3 w-3" />
            <span>{statusLabel}</span>
            {images.length > 0 && onValidationClick && (
              <span className="text-gray-500">(see details)</span>
            )}
          </div>
        </div>
      </div>

      {/* Image Preview Area */}
      <div
        className="mt-2 relative"
        onClick={handleClick}
      >
        {images.length === 0 ? (
          // Empty state - show upload prompt
          <div className="aspect-video bg-gray-100 rounded border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:bg-gray-50 transition-colors">
            <Upload className="h-8 w-8 mb-1" />
            <span className="text-xs">Click to upload</span>
          </div>
        ) : images.length === 1 ? (
          // Single image - show full preview with optional re-upload overlay
          <div className="aspect-video bg-gray-900 rounded overflow-hidden relative">
            {imageSrc ? (
              <img
                src={imageSrc}
                alt={`${section.name} - ${images[0].image_name}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Package className="h-8 w-8 text-gray-400 animate-pulse" />
              </div>
            )}

            {/* Re-upload overlay - only on image area */}
            {showReuploadButton && (
              <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all rounded flex items-center justify-center opacity-0 group-hover:opacity-100 z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onEmptyClick(); // Trigger re-upload
                  }}
                  className="px-4 py-2 bg-white text-gray-900 rounded-lg font-medium hover:bg-gray-100 transition-colors shadow-lg flex items-center gap-2"
                >
                  <Upload className="h-4 w-4" />
                  Re-upload Image
                </button>
              </div>
            )}
          </div>
        ) : (
          // Multiple images - show 2x2 grid
          <div className="grid grid-cols-2 gap-1">
            {images.slice(0, 4).map((image, idx) => (
              <div
                key={image.id || idx}
                className="aspect-square bg-gray-900 rounded overflow-hidden"
              >
                <img
                  src={image.visualization_path || image.image_path}
                  alt={`${section.name} - ${image.image_name}`}
                  className="w-full h-full object-cover"
                />
              </div>
            ))}
            {images.length > 4 && (
              <div className="aspect-square bg-gray-800 rounded flex items-center justify-center text-white text-sm font-medium">
                +{images.length - 4}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Image Count (if multiple) */}
      {images.length > 1 && (
        <div className="mt-2 text-xs text-gray-600 text-center">
          {images.length} image{images.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Expected Items Count or Validation Counts */}
      {section.expected_items.length > 0 && (
        <div className="mt-2">
          <div className="text-xs text-gray-500 flex items-center gap-1">
            <Package className="h-3 w-3" />
            {validationCounts ? (
              <span className="flex items-center gap-0.5 font-medium">
                <span className={VALIDATION_COLORS.match}>{validationCounts.match}</span>
                <span className="text-gray-400">/</span>
                <span className={VALIDATION_COLORS.mismatch}>{validationCounts.mismatch}</span>
                <span className="text-gray-400">/</span>
                <span className={VALIDATION_COLORS.missing}>{validationCounts.missing}</span>
                <span className="text-gray-400">/</span>
                <span className={VALIDATION_COLORS.unexpected}>{validationCounts.unexpected}</span>
              </span>
            ) : (
              <span>{section.expected_items.length} expected item types</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SectionGridItem;
